import { useState, useEffect, useMemo, lazy, Suspense, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MdCalendarToday, MdPayment, MdNotifications, MdClose, MdExpandMore, MdExpandLess, MdSearch, MdVerified, MdStar, MdLock, MdLocationOn, MdWork, MdPerson, MdAccessTime, MdFavoriteBorder, MdChevronRight, MdTune, MdLogin, MdPersonAdd } from 'react-icons/md';
import { FaRobot } from 'react-icons/fa';
import ThemeToggle from '../components/ThemeToggle';
import LanguageSelector from '../components/LanguageSelector';
import Footer from '../components/Footer';
import AnimatedBackground from '../components/AnimatedBackground';
import SubtleGridBackground from '../components/SubtleGridBackground';
import LocationAutocomplete from '../components/LocationAutocomplete';
import ResultsList from '../components/search/ResultsList';
import MapPanel from '../components/search/MapPanel';
import FiltersDrawer, { DEFAULT_FILTERS } from '../components/search/FiltersDrawer';
import ResultCard from '../components/search/ResultCard';
import ResultsToolbar from '../components/search/ResultsToolbar';
import AuthModal from '../components/auth/AuthModal';
import api from '../services/api';
import { getUser } from '../services/auth';
import { t, getLanguage } from '../utils/i18n';
import logoImage from '../assets/babysit_logo.png';

// Helper function to get avatar image from RandomUser.me API (free, no API key needed)
// Uses seed based on user ID to get consistent images
const getAvatarImage = (userId, firstName, lastName) => {
  if (!userId) return null;
  // Use user ID as seed for consistent image per user
  const seed = userId.toString().replace(/[^a-zA-Z0-9]/g, '').slice(0, 10) || 'default';
  const name = `${firstName || ''} ${lastName || ''}`.trim() || 'User';
  // RandomUser.me API - returns random but consistent image based on seed
  return `https://api.dicebear.com/7.x/avataaars/svg?seed=${seed}&backgroundColor=b6e3ff,c0aede,d1d4f9,ffd5dc,ffdfbf`;
};

export default function LandingPage() {
  const navigate = useNavigate();
  const user = getUser();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [, setLang] = useState(getLanguage());
  const [openFAQ, setOpenFAQ] = useState(null);
  const [testimonialAvatars, setTestimonialAvatars] = useState({});
  const [searchMode, setSearchMode] = useState('parent'); // 'parent' or 'sitter'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'list' for results
  // Raw results loaded from API (used as "catalog" for map + list pagination)
  const [searchResults, setSearchResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [isNearbySearch, setIsNearbySearch] = useState(false); // Flag za pretragu u blizini (2km)
  const [hoveredItemId, setHoveredItemId] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedPinItemIds, setSelectedPinItemIds] = useState(null); // null = svi; array = filtrirano po pin-u
  const [mapBounds, setMapBounds] = useState(null);
  const lastMapBoundsRef = useRef(null);
  const [appliedListBounds, setAppliedListBounds] = useState(null);
  // Always ON: list follows current map bounds (no toggle)
  const [searchAsIMove] = useState(true);
  const [searchError, setSearchError] = useState(null);
  const [sortBy, setSortBy] = useState('default');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [resultAvatars, setResultAvatars] = useState({});
  const lastSearchRef = useRef(null);

  const locationAutocompleteRef = useRef(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    company: '', // honeypot
  });
  const [contactStatus, setContactStatus] = useState({ state: 'idle', message: '' }); // idle|sending|success|error
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
  const [registerPromptTarget, setRegisterPromptTarget] = useState(null); // path to go after login/register
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');

  const openAuthModal = (mode = 'login', redirect = null) => {
    setRegisterPromptTarget(redirect);
    setAuthModalMode(mode);
    setAuthModalOpen(true);
  };

  useEffect(() => {
    const handleLanguageChange = () => {
      setLang(getLanguage());
    };
    
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  // Bbox oko tačke (radius u km) — za API north/south/east/west
  const getBboxFromCenter = (lat, lon, radiusKm = 2) => {
    const latDelta = radiusKm / 111.0;
    const lonDelta = radiusKm / (111.0 * Math.cos((lat * Math.PI) / 180));
    return {
      south: lat - latDelta,
      north: lat + latDelta,
      west: lon - lonDelta,
      east: lon + lonDelta,
    };
  };

  // Izračunaj udaljenost između dve tačke (Haversine formula, rezultat u km)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius Zemlje u km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const handleLocationSelect = (location) => {
    const isNearby = location.type === 'nearby';
    setIsNearbySearch(isNearby);
    setSelectedLocation(location);
    setSearchQuery(location.label);
    // Mapa će se prikazati automatski kada selectedLocation postane setovan
    // Fetch search results for this location
    fetchSearchResults(location, isNearby);
  };

  // Normalizuj grad za API. Manje naselje / opština MORA pre širokog „Beograda“ — OSM često vrati grad=Beograd za Lazarevac itd.
  const normalizeCityForApi = (cityOrLabel) => {
    if (!cityOrLabel) return undefined;
    const s = String(cityOrLabel).toLowerCase();
    if (/lazarevac|лазаревац/i.test(s)) return 'Lazarevac';
    if (/lajkovac|лајковац/i.test(s)) return 'Lajkovac';
    if (/novi\s*sad|нови\s*сад/i.test(s)) return 'Novi Sad';
    if (/niš|nis|ниш/i.test(s)) return 'Niš';
    if (/pančevo|pancevo|панчево/i.test(s)) return 'Pančevo';
    const isBelgrade =
      /belgrade|^beograd\b|београд\b|stari\s*grad|стари\s*град|градска\s*општина|општина\s*стари/i.test(s) ||
      s.includes('beograd') ||
      s.includes('београд');
    if (isBelgrade) return 'Beograd';
    return cityOrLabel === 'Belgrade' ? 'Beograd' : cityOrLabel;
  };

  const PAGE_SIZE = 24;
  /** Koliko oglasa povući za mapu (mora biti u skladu sa backend GET /jobs/public max limit). */
  const JOBS_MAP_CATALOG_LIMIT = 1500;

  const fetchSearchResults = async (location, nearbyMode = false, page = 1, append = false) => {
    if (page === 1) {
      setLoadingResults(true);
      setSearchError(null);
    } else setLoadingMore(true);
    try {
      let allResults = [];

      if (searchMode === 'parent') {
        const cityForApi = location.city || location.label?.split(',')[0]?.trim();
        const cityParam = nearbyMode ? undefined : normalizeCityForApi(cityForApi) || undefined;
        // Za mapu moramo imati "sve" rezultate (u razumnom limitu) da bi pinovi uvek bili kompletni u trenutnom view-u.
        // Lista levo ostaje paginirana preko PAGE_SIZE (slice), bez dodatnog API poziva.
        const limit = 500;
        const response = await api.get('/users/providers', {
          params: { city: cityParam, limit, page: 1 }
        });
        allResults = response.data?.providers || [];
      } else {
        // Jobs (sitter mode): bez „uvek bbox oko tačke” — to je na gustom Beogradu ispunjavalo svih 500 rezultata
        // samo BG pinovima i skrivalo NS, Pančevo, Lazarevac itd. Bbox samo za pravu pretragu „u blizini”.
        // Inače: opcioni filter po gradu (iz autocomplete-a / naslova); ako nema grada — nacionalno (mešavina).
        const params = { limit: JOBS_MAP_CATALOG_LIMIT, page };
        if (nearbyMode && location?.lat != null && location?.lon != null) {
          const bbox = getBboxFromCenter(Number(location.lat), Number(location.lon), 5);
          params.north = bbox.north;
          params.south = bbox.south;
          params.east = bbox.east;
          params.west = bbox.west;
        } else {
          const rawCity = location?.city || location?.label?.split(',')[0]?.trim();
          const cityForApi = normalizeCityForApi(rawCity);
          if (cityForApi) params.city = cityForApi;
        }
        const response = await api.get('/jobs/public', { params }).catch(() => ({ data: { jobs: [] } }));
        allResults = response.data?.jobs || [];
        const pg = response.data?.pagination;
        if (pg?.pages != null && pg?.current != null) {
          setHasMoreResults(Number(pg.current) < Number(pg.pages));
        } else {
          setHasMoreResults(allResults.length >= JOBS_MAP_CATALOG_LIMIT);
        }
      }

      // Za parent mode nearby: filtriraj po 2km; za jobs bbox već ograničava na API-ju
      let list = allResults;
      if (searchMode === 'parent' && nearbyMode && location.lat && location.lon) {
        list = allResults.filter(item => {
          const itemLat = item.address?.lat || item.address?.latitude;
          const itemLon = item.address?.lng || item.address?.longitude;
          if (!itemLat || !itemLon) return false;
          const distance = calculateDistance(location.lat, location.lon, parseFloat(itemLat), parseFloat(itemLon));
          return distance <= 2.0;
        });
        setHasMoreResults(false);
      }

      if (searchMode === 'parent') {
        // One-shot catalog for map + list pagination
        setSearchResults(list);
        setSearchPage(1);
        setSelectedPinItemIds(null);
        setMapBounds(null); // nova pretraga — prikaži sve dok mapa ne pošalje bounds
        lastSearchRef.current = { location, nearbyMode };
      } else {
        // Jobs: server paging as before
        if (append) {
          setSearchResults(prev => [...prev, ...list]);
          setSearchPage(page);
        } else {
          setSearchResults(list);
          setSearchPage(1);
          setSelectedPinItemIds(null);
          setMapBounds(null);
          lastSearchRef.current = { location, nearbyMode };
        }
      }
    } catch (error) {
      console.error('Error fetching search results:', error);
      setSearchError(error?.message || 'Greška pri učitavanju');
      if (!append) setSearchResults([]);
    } finally {
      setLoadingResults(false);
      setLoadingMore(false);
    }
  };

  const loadMoreResults = () => {
    const last = lastSearchRef.current;
    if (!last || loadingMore || !hasMoreResults) return;
    if (searchMode === 'parent') {
      // Local paging over catalog (no extra API call)
      setSearchPage((p) => p + 1);
      return;
    }
    fetchSearchResults(last.location, last.nearbyMode, searchPage + 1, true);
  };

  // Rezultati u vidljivoj oblasti mape (zoom/pan osvežava); kad nema bounds prikaži sve
  const getItemLatLng = (item) => {
    const addr = searchMode === 'parent' ? item.address : item.location;
    const lat = addr?.lat != null ? parseFloat(addr.lat) : null;
    const lng = (addr?.lng != null ? parseFloat(addr.lng) : addr?.lon != null ? parseFloat(addr.lon) : null);
    return { lat, lng };
  };
  // Filtered + sorted catalog (shared for map + list)
  const baseResults = useMemo(() => {
    let list = searchResults;
    // Client-side filters
    if (filters.priceMin !== '' || filters.priceMax !== '') {
      const min = filters.priceMin !== '' ? Number(filters.priceMin) : 0;
      const max = filters.priceMax !== '' ? Number(filters.priceMax) : Infinity;
      list = list.filter((item) => {
        const rate = item.hourlyRate ?? 0;
        return rate >= min && rate <= max;
      });
    }
    if (filters.ratingMin !== '') {
      const minR = Number(filters.ratingMin);
      list = list.filter((item) => (item.rating?.average ?? 0) >= minR);
    }
    if (filters.verifiedOnly) {
      list = list.filter((item) => !!item.verified);
    }
    if (sortBy === 'price_asc') list = [...list].sort((a, b) => (a.hourlyRate ?? 0) - (b.hourlyRate ?? 0));
    else if (sortBy === 'price_desc') list = [...list].sort((a, b) => (b.hourlyRate ?? 0) - (a.hourlyRate ?? 0));
    else if (sortBy === 'rating') list = [...list].sort((a, b) => (b.rating?.average ?? 0) - (a.rating?.average ?? 0));
    return list;
  }, [searchResults, sortBy, filters]);

  const selectedItem = useMemo(() => {
    if (!selectedItemId) return null;
    return baseResults.find((x) => x?.id === selectedItemId) || null;
  }, [baseResults, selectedItemId]);

  // LIST filtering behavior:
  // - If searchAsIMove ON: list follows current map bounds
  // - Else: list follows "applied" bounds only after clicking CTA (Pretraži ovu oblast)
  // - If selectedPinItemIds: filter to only those items (pin/cluster click)
  const listBase = useMemo(() => {
    const bounds = searchAsIMove ? mapBounds : appliedListBounds;
    let base = baseResults;
    if (bounds) {
      const { north, south, east, west } = bounds;
      base = base.filter((item) => {
        const { lat, lng } = getItemLatLng(item);
        if (lat == null || lng == null) return false;
        return lat >= south && lat <= north && lng >= west && lng <= east;
      });
    }
    if (selectedPinItemIds && selectedPinItemIds.length > 0) {
      const idSet = new Set(selectedPinItemIds);
      base = base.filter((item) => idSet.has(item.id));
    }
    return base;
  }, [baseResults, searchAsIMove, mapBounds, appliedListBounds, selectedPinItemIds]);

  // LIST: paginated (load more) over catalog
  const listResults = useMemo(() => {
    if (searchMode !== 'parent') return listBase;
    return listBase.slice(0, searchPage * PAGE_SIZE);
  }, [listBase, searchMode, searchPage]);

  // MAP: always show pins for the *currently visible* map bounds
  const mapResults = useMemo(() => {
    if (!mapBounds || baseResults.length === 0) return baseResults;
    const { north, south, east, west } = mapBounds;
    return baseResults.filter((item) => {
      const { lat, lng } = getItemLatLng(item);
      if (lat == null || lng == null) return false;
      return lat >= south && lat <= north && lng >= west && lng <= east;
    });
  }, [baseResults, mapBounds]);

  // hasMore depends on catalog size (parent) or server paging (jobs)
  useEffect(() => {
    if (searchMode !== 'parent') return;
    setHasMoreResults(listResults.length < listBase.length);
  }, [searchMode, listResults.length, listBase.length]);

  const handleSearch = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!searchQuery.trim()) return;
    
    locationAutocompleteRef.current?.closeDropdown();
    
    // Ne navigirati na /dashboard/jobs — uvek ostajemo na landingu i prikazujemo rezultate ovde.
    let locationToUse = selectedLocation;
    
    if (!locationToUse || !locationToUse.lat) {
      // Ako nije izabrano iz dropdown-a (Nominatim može biti blokiran), radi samo city-based search bez geokodiranja
      locationToUse = { label: searchQuery.trim() };
      setSelectedLocation(locationToUse);
    }
    
    await fetchSearchResults(locationToUse);
  };

  const submitContact = async (e) => {
    e.preventDefault();
    if (contactStatus.state === 'sending') return;

    setContactStatus({ state: 'sending', message: '' });
    try {
      await api.post('/contact', {
        name: contactForm.name,
        email: contactForm.email,
        subject: contactForm.subject,
        message: contactForm.message,
        company: contactForm.company,
      });
      setContactStatus({ state: 'success', message: 'Poruka je poslata. Javićemo se uskoro.' });
      setContactForm({ name: '', email: '', subject: '', message: '', company: '' });
    } catch (err) {
      setContactStatus({
        state: 'error',
        message: err?.response?.data?.message || 'Greška pri slanju poruke. Pokušajte ponovo.',
      });
    }
  };

  // Load demo avatar images for testimonials (free API, cached)
  useEffect(() => {
    const CACHE_KEY = 'testimonial_avatars_v2';
    const CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 14; // 14 days

    const readCache = () => {
      try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.data || !parsed?.savedAt) return null;
        if (Date.now() - parsed.savedAt > CACHE_TTL_MS) return null;
        return parsed.data;
      } catch {
        return null;
      }
    };

    const writeCache = (data) => {
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ savedAt: Date.now(), data }));
      } catch {
        // ignore
      }
    };

    const cached = readCache();
    if (cached) {
      setTestimonialAvatars(cached);
      return;
    }

    const controller = new AbortController();
    (async () => {
      try {
        // Seeded results so avatars stay stable between refreshes
        const resp = await fetch('https://randomuser.me/api/?seed=babysitter-tracker-testimonials&results=4&inc=picture&gender=female', {
          signal: controller.signal,
        });
        if (!resp.ok) return;
        const json = await resp.json();
        const pictures = (json?.results || []).map((r) => r?.picture?.large).filter(Boolean);
        if (pictures.length === 0) return;

        const mapping = {
          // Platformu uglavnom koriste žene (~90%) — svi testimonials žene
          1: pictures[0] || null,
          2: pictures[1] || null,
          3: pictures[2] || null,
          4: pictures[3] || null,
        };
        setTestimonialAvatars(mapping);
        writeCache(mapping);
      } catch (e) {
        // ignore (offline/adblock/etc)
      }
    })();

    return () => controller.abort();
  }, []);

  // Avatari stvarnih ljudi za rezultate pretrage (kad nema photoUrl) — randomuser.me po id-u
  useEffect(() => {
    if (searchResults.length === 0) return;
    const needAvatar = searchResults.filter((item) => !(item.photoUrl || item.photo_url || item.profileImage));
    if (needAvatar.length === 0) return;
    const CACHE_KEY = 'landing_result_avatars_v1';
    const cache = (() => {
      try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return {};
        const { data, savedAt } = JSON.parse(raw);
        if (Date.now() - savedAt > 14 * 24 * 60 * 60 * 1000) return {};
        return data || {};
      } catch { return {}; }
    })();
    const toFetch = needAvatar.filter((item) => !cache[item.id]);
    if (toFetch.length === 0) {
      setResultAvatars((prev) => ({ ...prev, ...cache }));
      return;
    }
    const controller = new AbortController();
    (async () => {
      const next = { ...cache };
      for (const item of toFetch.slice(0, 30)) {
        try {
          const r = await fetch(
            `https://randomuser.me/api/?seed=${item.id}&results=1&inc=picture&gender=female`,
            { signal: controller.signal }
          );
          if (!r.ok) continue;
          const j = await r.json();
          const pic = j?.results?.[0]?.picture?.medium;
          if (pic) next[item.id] = pic;
        } catch {
          // ignore
        }
      }
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ data: next, savedAt: Date.now() }));
      } catch {}
      setResultAvatars((prev) => ({ ...prev, ...next }));
    })();
    return () => controller.abort();
  }, [searchResults.map((i) => i.id).join(',')]);

  const testimonials = [
    {
      id: 1,
      name: 'Ana Marković',
      role: 'Dadilja',
      city: 'Beograd',
      text: 'Fantastična platforma! Sada mogu da pratim sve svoje poslove i zaradu na jednom mestu. Automatsko izračunavanje plaćanja mi štedi vreme.',
      rating: 5,
    },
    {
      id: 2,
      name: 'Marko Petrović',
      role: 'Otac dvoje dece',
      city: 'Novi Sad',
      text: 'Pronašao sam izvrsnu dadilju preko Nannity-ja. Praćenje sati je automatsko, a plaćanja su jednostavna. Preporučujem svim roditeljima!',
      rating: 5,
    },
    {
      id: 3,
      name: 'Jovana Stojanović',
      role: 'Majka jednog deteta',
      city: 'Niš',
      text: 'Kroz Nannity sam pronašla najbolju dadilju za moje dete. AI pametno uparivanje stvarno funkcioniše - sve je bilo po meri!',
      rating: 5,
    },
    {
      id: 4,
      name: 'Petar Nikolić',
      role: 'Dadilja',
      city: 'Beograd',
      text: 'Najbolja stvar je što mogu da povezujem svoj profil sa roditeljima i zajedno pratimo satnicu i plaćanja. Sve je transparentno i profesionalno.',
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden bg-gradient-to-br from-primary-100 via-white to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <AnimatedBackground />
      <SubtleGridBackground opacityClass="opacity-[0.015] dark:opacity-[0.02]" />
      <div className="z-10">
        {/* Header */}
        <header className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <nav className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={logoImage} 
              alt="Nannity Logo" 
              className="h-10 sm:h-12 w-auto"
            />
            <span className="text-xl sm:text-2xl font-bold gradient-bg bg-clip-text text-transparent">
              Nannity
            </span>
            <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border border-primary-200 bg-primary-50/70 text-primary-700 dark:bg-primary-900/20 dark:border-primary-800 dark:text-primary-200">
              Babysitter Tracker <span aria-hidden="true">♥</span>
            </span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-2 sm:gap-4 items-center">
            <LanguageSelector />
            <ThemeToggle />
            <button 
              type="button"
              onClick={() => openAuthModal('login')}
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold px-3 py-2"
            >
              Uloguj se
            </button>
            <button 
              type="button"
              onClick={() => openAuthModal('register')}
              className="btn-primary text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3"
            >
              Registruj se
            </button>
          </div>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSelector />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-xl bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              aria-label={t('nav.accountMenu') || 'Meni naloga'}
            >
              {isMobileMenuOpen ? (
                <MdClose className="text-xl" />
              ) : (
                <MdPerson className="text-xl" />
              )}
            </button>
          </div>
        </nav>
        
        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <div className="container mx-auto px-4 py-4 space-y-1">
              <div className="flex items-center justify-between py-2.5 px-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ml-0">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  {t('nav.theme')}
                </span>
                <ThemeToggle />
              </div>
              <button
                type="button"
                onClick={() => { setIsMobileMenuOpen(false); openAuthModal('register'); }}
                className="flex items-center justify-between gap-3 w-full px-3 py-2.5 rounded-xl font-semibold border-2 border-primary-500 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              >
                <span>Registruj se</span>
                <MdPersonAdd className="text-xl text-primary-600 dark:text-primary-400 flex-shrink-0" />
              </button>
              <button
                type="button"
                onClick={() => { setIsMobileMenuOpen(false); openAuthModal('login'); }}
                className="flex items-center justify-between gap-3 w-full btn-primary text-center px-3 py-2.5 rounded-xl"
              >
                <span>Uloguj se</span>
                <MdLogin className="text-xl opacity-90 flex-shrink-0" />
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section with Search — overflow-visible so location dropdown is not clipped */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 lg:pt-20 pb-8 sm:pb-12 lg:pb-16 relative overflow-visible">
        {/* Parent širok do 1600px da mapa i lista oglasa ne budu ograničeni uskim max-w-4xl */}
        <div className="relative z-10 text-center animate-fade-in max-w-[1600px] mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold mb-3 sm:mb-4 tracking-tight">
            <span className="text-primary-600 dark:text-primary-400">
              {t('landing.hero.title')}
            </span>
            <br />
            <span className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-800 dark:text-gray-200">
              {t('landing.hero.subtitle')}
            </span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-6 sm:mb-8 max-w-2xl mx-auto px-2 sm:px-4 leading-relaxed animate-fade-in" style={{ animationDelay: '0.2s' }}>
            {t('landing.hero.description')}
          </p>
          
          {/* Search Bar - Pill Style (samo traka ostaje u max-w-3xl) */}
          <div className="max-w-3xl mx-auto px-2 sm:px-4 animate-fade-in" style={{ animationDelay: '0.35s' }}>
            <form onSubmit={handleSearch} className="relative">
              {/* Mode Toggle - Subtle */}
              <div className="flex items-center justify-center mb-3">
                <div className="inline-flex items-center rounded-full border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-1 shadow-sm">
                  <button
                    type="button"
                    onClick={() => setSearchMode('parent')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                      searchMode === 'parent'
                        ? 'bg-primary-600 text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    Tražim dadilju
                  </button>
                  <button
                    type="button"
                    onClick={() => setSearchMode('sitter')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                      searchMode === 'sitter'
                        ? 'bg-primary-600 text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    Tražim posao
                  </button>
                </div>
              </div>

              {/* Search Input + Button — nizak z-index da rezultati/bottom sheet budu iznad; dropdown lokacije je u portalu sa visokim z-index */}
              <div className="relative z-10">
                <div className="flex flex-col sm:flex-row gap-0 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-slate-200/60 dark:border-gray-700 overflow-visible focus-within:ring-2 focus-within:ring-primary-500/50 focus-within:border-primary-500 dark:focus-within:border-primary-400 transition-all sm:overflow-visible">
                  <div className="flex-1 relative rounded-t-full sm:rounded-l-full sm:rounded-r-none z-10">
                    <LocationAutocomplete
                      ref={locationAutocompleteRef}
                      value={searchQuery}
                      onChange={setSearchQuery}
                      onSelect={handleLocationSelect}
                      placeholder="Unesite grad ili naselje…"
                      className="w-full"
                      debounceMs={300}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          locationAutocompleteRef.current?.closeDropdown();
                          handleSearch(e);
                        }
                      }}
                    />
                  </div>
                  <button
                    type="submit"
                    onClick={() => {
                      locationAutocompleteRef.current?.closeDropdown();
                    }}
                    className="btn-primary rounded-b-full sm:rounded-r-full sm:rounded-l-none rounded-t-none px-4 sm:px-8 sm:px-10 py-4 text-base sm:text-lg font-semibold whitespace-nowrap w-full sm:w-auto"
                  >
                    {searchMode === 'parent' ? 'Pronađi dadilju' : 'Pronađi posao'}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Search results + map — premium marketplace layout */}
          {((selectedLocation && selectedLocation.lat && selectedLocation.lon) || searchResults.length > 0 || loadingResults) && (
            <div className="mt-6 w-full px-4 sm:px-6 lg:px-8 animate-fade-in relative z-20">
              <div className="max-w-[1600px] mx-auto">
                <ResultsToolbar
                  count={listResults.length}
                  totalCount={baseResults.length}
                  locationLabel={searchQuery || undefined}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  onOpenFilters={() => setShowFilters((v) => !v)}
                  filtersOpen={showFilters}
                  viewMode={viewMode}
                  onViewModeChange={(next) => {
                    if (next === 'list') setSelectedItemId(null);
                    setViewMode(next);
                  }}
                  onClear={() => {
                    setSelectedLocation(null);
                    setSearchQuery('');
                    setSearchResults([]);
                    setSelectedItemId(null);
                    setSelectedPinItemIds(null);
                    setShowFilters(false);
                  }}
                />

                {/* Filters drawer: mobile = full-screen, desktop = inline panel below bar when open */}
                <div className="mb-4">
                  <div className="lg:hidden">
                    <FiltersDrawer
                      open={showFilters}
                      onClose={() => setShowFilters(false)}
                      filters={filters}
                      onApply={setFilters}
                      onReset={() => setFilters(DEFAULT_FILTERS)}
                      isMobile={true}
                    />
                  </div>
                  <div className="hidden lg:block">
                    {showFilters && (
                      <FiltersDrawer
                        open={showFilters}
                        onClose={() => setShowFilters(false)}
                        filters={filters}
                        onApply={setFilters}
                        onReset={() => setFilters(DEFAULT_FILTERS)}
                        isMobile={false}
                      />
                    )}
                  </div>
                </div>

                {/* Desktop: 50/50 lista i mapa. Mobile: bira se Mapa ili Lista */}
                <div className="relative grid grid-cols-1 lg:grid-cols-[minmax(420px,42%)_minmax(0,58%)] gap-4 min-h-0">
                  {/* Lista */}
                  <div
                    className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-4 lg:p-5 max-h-[calc(100vh-12rem)] min-h-[520px] overflow-y-auto min-w-0 ${
                      viewMode === 'map' ? 'hidden lg:block' : 'block'
                    }`}
                  >
                    {/* Mobile: dugme Nazad na mapu kada je prikaz Liste */}
                    {viewMode === 'list' && (
                      <div className="lg:hidden mb-3">
                        <button
                          type="button"
                          onClick={() => setViewMode('map')}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                        >
                          ← Nazad na mapu
                        </button>
                      </div>
                    )}
                    {selectedPinItemIds && selectedPinItemIds.length > 0 && (
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Prikazano {listBase.length} od pin-a na mapi
                        </span>
                        <button
                          type="button"
                          onClick={() => setSelectedPinItemIds(null)}
                          className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                        >
                          Prikaži sve
                        </button>
                      </div>
                    )}
                    <ResultsList
                      items={listResults}
                      isParent={searchMode === 'parent'}
                      getAvatarUrl={(item) => item.photoUrl || item.photo_url || item.profileImage || resultAvatars[item.id] || (searchMode === 'parent' ? getAvatarImage(item.id, item.firstName, item.lastName) : null)}
                      selectedItemId={selectedItemId}
                      hoveredItemId={hoveredItemId}
                      scrollToHovered={false}
                      onSelectItem={(item) => {
                        const next = selectedItemId === item.id ? null : item.id;
                        setSelectedItemId(next);
                        if (next && searchMode === 'parent') {
                          navigate(`/babysitter/${item.id}`);
                        } else if (next && searchMode !== 'parent') {
                          window.open(`/job/${item.id}`, '_blank', 'noopener,noreferrer');
                        }
                      }}
                      onContactClick={searchMode === 'parent' ? (item) => {
                        if (user) {
                          navigate(`/dashboard/conversations?userId=${item.id}`);
                        } else {
                          const target = `/dashboard/conversations?userId=${item.id}`;
                          setRegisterPromptTarget(target);
                          setShowRegisterPrompt(true);
                        }
                      } : undefined}
                      onHoverItem={setHoveredItemId}
                      loading={loadingResults}
                      error={searchError}
                      onRetry={() => lastSearchRef.current && fetchSearchResults(lastSearchRef.current.location, lastSearchRef.current.nearbyMode)}
                      hasMoreResults={hasMoreResults}
                      loadingMore={loadingMore}
                      onLoadMore={loadMoreResults}
                      emptyInBounds={false}
                      emptyVariant={listResults.length === 0 && (selectedLocation?.lat != null || lastSearchRef.current?.location?.lat != null) ? 'noResultsInArea' : 'default'}
                      emptyVariantProps={{
                        isParent: searchMode === 'parent',
                        onClear: () => {
                          setSelectedLocation(null);
                          setSearchQuery('');
                          setSearchResults([]);
                          setSelectedItemId(null);
                          setSelectedPinItemIds(null);
                          setShowFilters(false);
                        },
                      }}
                      listHeight={520}
                    />
                  </div>

                  {/* Mapa */}
                  <div
                    className={`rounded-2xl overflow-hidden h-[400px] min-h-[360px] lg:h-[calc(100vh-12rem)] lg:min-h-[520px] lg:min-h-0 w-full block ${
                      viewMode === 'list' ? 'hidden lg:block' : 'block'
                    }`}
                  >
                    <MapPanel
                      key={selectedLocation?.lat != null ? `${selectedLocation.lat}-${selectedLocation.lon}` : 'no-location'}
                      className="h-full min-h-0"
                      showMapFooter={false}
                      isVisible={viewMode === 'map'}
                      markerHoverTooltipOnly
                      items={mapResults}
                      isParent={searchMode === 'parent'}
                      userAddress={selectedLocation?.lat != null && selectedLocation?.lon != null ? { lat: selectedLocation.lat, lng: selectedLocation.lon, city: selectedLocation.city || selectedLocation.label?.split(',')[0], label: selectedLocation.label, nearby: isNearbySearch } : null}
                      friends={[]}
                      selectedItemId={selectedItemId}
                      hoveredItemId={hoveredItemId}
                      onMapMove={(bounds) => {
                        const prev = lastMapBoundsRef.current;
                        const eps = 1e-6;
                        const same =
                          prev &&
                          Math.abs(prev.north - bounds.north) < eps &&
                          Math.abs(prev.south - bounds.south) < eps &&
                          Math.abs(prev.east - bounds.east) < eps &&
                          Math.abs(prev.west - bounds.west) < eps;
                        if (!same) {
                          lastMapBoundsRef.current = bounds;
                          setMapBounds(bounds);
                        }
                      }}
                      showSearchThisArea={false}
                      onSearchThisArea={() => {
                        // Apply current map bounds to LIST (one-shot) and reset pagination
                        setAppliedListBounds(mapBounds);
                        setSearchPage(1);
                      }}
                      onMarkerClick={(itemOrItems) => {
                        const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
                        const ids = items.map((i) => i.id).filter(Boolean);
                        if (ids.length > 0) {
                          setSelectedPinItemIds(ids);
                          setViewMode('list');
                        }
                      }}
                      onMarkerHover={setHoveredItemId}
                      searchAsIMove={true}
                      getItemHref={(item) => (searchMode === 'parent' ? `/babysitter/${item.id}` : `/job/${item.id}`)}
                      onRecenter={() => {}}
                    />
                  </div>
                </div>

                {/* Mobile: pin preview (bottom) */}
                {viewMode === 'map' && selectedItem && (
                  <div className="lg:hidden fixed left-0 right-0 bottom-0 z-[70] px-4 pb-4">
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Pregled</span>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                          onClick={() => setSelectedItemId(null)}
                          aria-label="Zatvori pregled"
                        >
                          <MdClose className="text-lg" />
                        </button>
                      </div>
                      <ResultCard
                        item={selectedItem}
                        isParent={searchMode === 'parent'}
                        avatarUrl={selectedItem.photoUrl || selectedItem.photo_url || selectedItem.profileImage || resultAvatars[selectedItem.id]}
                        initials={(searchMode === 'parent' ? `${selectedItem.firstName?.[0] || ''}${selectedItem.lastName?.[0] || ''}`.toUpperCase() : 'P')}
                        isSelected={true}
                        isHovered={false}
                        onClick={() => {
                          if (searchMode === 'parent') {
                            navigate(`/babysitter/${selectedItem.id}`);
                          } else {
                            window.open(`/job/${selectedItem.id}`, '_blank', 'noopener,noreferrer');
                          }
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

            {/* Trust Row - Chips */}
            <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-full text-xs sm:text-sm font-medium text-primary-700 dark:text-primary-300">
                <MdVerified className="text-primary-600 dark:text-primary-400 text-base" />
                <span>Provereni profili</span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-full text-xs sm:text-sm font-medium text-primary-700 dark:text-primary-300">
                <MdStar className="text-primary-600 dark:text-primary-400 text-base" />
                <span>Ocene</span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-full text-xs sm:text-sm font-medium text-primary-700 dark:text-primary-300">
                <MdLock className="text-primary-600 dark:text-primary-400 text-base" />
                <span>Sigurno plaćanje</span>
              </div>
            </div>
          </div>
      </section>

      {/* Mobile-only CTA above "Kako funkcioniše" */}
      <div className="md:hidden container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl pt-2 pb-4 flex justify-center">
        <button
          type="button"
          onClick={() => openAuthModal('register')}
          className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border-2 border-primary-600 text-primary-600 hover:bg-primary-50 dark:border-primary-400 dark:text-primary-400 dark:hover:bg-primary-900/20 font-semibold text-base transition-colors"
        >
          Registruj se besplatno
        </button>
      </div>

      {/* How It Works Section - Inline Stepper */}
      <section className="py-8 sm:py-10 lg:py-12 bg-gradient-to-b from-transparent via-white/50 to-white dark:via-gray-900/50 dark:to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Kako funkcioniše
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Tri jednostavna koraka do pronalaska idealne dadilje
            </p>
          </div>
          {/* Inline Steps - Desktop */}
          <div className="hidden md:flex items-center justify-center gap-4 lg:gap-8">
            <div className="flex items-center gap-3 lg:gap-4 flex-1 max-w-xs">
              <div className="flex-shrink-0 w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-primary-600 text-white flex items-center justify-center text-base lg:text-lg font-bold">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Pretraži</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Pronađi dadilju u svom gradu ili naselju</p>
              </div>
            </div>
            <div className="flex-shrink-0 w-8 lg:w-12 h-px bg-gray-300 dark:bg-gray-600"></div>
            <div className="flex items-center gap-3 lg:gap-4 flex-1 max-w-xs">
              <div className="flex-shrink-0 w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-primary-600 text-white flex items-center justify-center text-base lg:text-lg font-bold">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Kontaktiraj</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Poveži se sa dadiljom i dogovori detalje</p>
              </div>
            </div>
            <div className="flex-shrink-0 w-8 lg:w-12 h-px bg-gray-300 dark:bg-gray-600"></div>
            <div className="flex items-center gap-3 lg:gap-4 flex-1 max-w-xs">
              <div className="flex-shrink-0 w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-primary-600 text-white flex items-center justify-center text-base lg:text-lg font-bold">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Započni</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Prati satnicu i plaćanja na jednom mestu</p>
              </div>
            </div>
          </div>
          {/* Stacked Steps - Mobile */}
          <div className="md:hidden space-y-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-600 text-white flex items-center justify-center text-base font-bold">
                1
              </div>
              <div className="flex-1 pt-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">Pretraži</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Pronađi dadilju u svom gradu ili naselju</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-600 text-white flex items-center justify-center text-base font-bold">
                2
              </div>
              <div className="flex-1 pt-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">Kontaktiraj</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Poveži se sa dadiljom i dogovori detalje</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-600 text-white flex items-center justify-center text-base font-bold">
                3
              </div>
              <div className="flex-1 pt-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">Započni</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Prati satnicu i plaćanja na jednom mestu</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Flat Design */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          {/* Section Header */}
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-100/80 dark:bg-primary-900/30 border border-primary-200/50 dark:border-primary-800/50 mb-6">
              <span className="w-2 h-2 rounded-full bg-primary-600 dark:bg-primary-400"></span>
              <p className="text-xs sm:text-sm font-bold text-primary-700 dark:text-primary-300 uppercase tracking-widest">
                Funkcionalnosti
              </p>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Sve što ti treba za jednostavno upravljanje poslom dadilje ili pronalaženje idealne dadilje
            </p>
          </div>

          {/* Flat Grid Layout - 2x2 on desktop, stacked on mobile */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
            {/* Feature 1 - Calendar */}
            <div className="group flex items-start gap-4 p-5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition-colors bg-white dark:bg-gray-800">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <MdCalendarToday className="text-xl text-primary-600 dark:text-primary-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
                  {t('landing.features.calendar.title')}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t('landing.features.calendar.description')}
                </p>
              </div>
            </div>

            {/* Feature 2 - Payments */}
            <div className="group flex items-start gap-4 p-5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition-colors bg-white dark:bg-gray-800">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <MdPayment className="text-xl text-primary-600 dark:text-primary-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
                  {t('landing.features.payment.title')}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t('landing.features.payment.description')}
                </p>
              </div>
            </div>

            {/* Feature 3 - Notifications */}
            <div className="group flex items-start gap-4 p-5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition-colors bg-white dark:bg-gray-800">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center">
                <MdNotifications className="text-xl text-pink-600 dark:text-pink-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
                  {t('landing.features.notifications.title')}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t('landing.features.notifications.description')}
                </p>
              </div>
            </div>

            {/* Feature 4 - AI */}
            <div className="group flex items-start gap-4 p-5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition-colors bg-white dark:bg-gray-800">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-primary-100 to-pink-100 dark:from-primary-900/30 dark:to-pink-900/30 flex items-center justify-center relative">
                <FaRobot className="text-xl text-primary-600 dark:text-primary-400" />
                <span className="absolute -top-1 -right-1 bg-gradient-to-br from-primary-600 via-pink-600 to-primary-700 text-white px-1.5 py-0.5 rounded-full text-[10px] font-black">
                  AI
                </span>
              </div>
              <div className="flex-1">
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
                  {t('landing.features.ai.title')}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t('landing.features.ai.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-20 lg:py-24 relative bg-gradient-to-b from-white via-gray-50/50 to-transparent dark:from-gray-900 dark:via-gray-800/50 dark:to-transparent">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          {/* Section Header */}
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight mb-4">
              {t('landing.testimonials.title')}
            </h2>
            <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300 max-w-2xl mx-auto">
              {t('landing.testimonials.description')}
            </p>
          </div>

          {/* Scrollable on mobile (flex + gap), Grid on desktop */}
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide md:overflow-x-visible md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-6 lg:gap-8 pb-4 md:pb-0">
            {testimonials.map((testimonial) => (
              <div 
                key={testimonial.id} 
                className="group relative bg-white/70 dark:bg-gray-800/70 backdrop-blur-md rounded-2xl p-6 sm:p-7 border border-white/40 dark:border-slate-700/60 shadow-sm hover:shadow-md hover:scale-[1.01] transition-all duration-300 min-w-[280px] sm:min-w-0 snap-center md:snap-none"
                style={{
                  backgroundImage: 'linear-gradient(to right, rgba(245, 51, 161, 0.02), transparent)',
                }}
              >
                {/* Quote mark ornament (very low opacity) */}
                <div className="absolute top-4 right-4 text-6xl sm:text-7xl font-serif text-primary-500/5 dark:text-primary-400/5 leading-none select-none">
                  "
                </div>

                {/* Avatar and Info */}
                <div className="flex items-start gap-4 mb-5 relative z-10">
                  <div className="relative flex-shrink-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary-500 to-pink-500 rounded-full blur-sm opacity-50"></div>
                    {testimonialAvatars[testimonial.id] ? (
                      <img
                        src={testimonialAvatars[testimonial.id]}
                        alt={`Avatar ${testimonial.name}`}
                        className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover shadow-md ring-2 ring-white/60 dark:ring-gray-900/40"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-primary-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg sm:text-xl shadow-md">
                        {testimonial.name[0]}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-gray-900 dark:text-gray-100 text-base sm:text-lg mb-1 truncate">
                      {testimonial.name}
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-0.5">
                      {testimonial.role}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      {testimonial.city}
                    </p>
                  </div>
                </div>

                {/* Rating Stars */}
                <div className="flex gap-1 mb-4 relative z-10">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-yellow-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>

                {/* Quote Text */}
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed relative z-10">
                  "{testimonial.text}"
                </p>

                {/* Gradient border on hover */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary-500/0 via-primary-500/10 to-pink-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Custom CSS for scrollbar hiding */}
        <style>{`
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </section>

      {/* Pricing Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-transparent via-white/30 to-white dark:via-gray-900/30 dark:to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-4 sm:mb-6 text-gray-900 dark:text-gray-100">
            {t('landing.pricing.title')}
          </h2>
          <p className="text-center text-gray-700 dark:text-gray-300 mb-8 sm:mb-12 max-w-2xl mx-auto">
            {t('landing.pricing.description')}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Free Plan */}
          <div className="card text-center border-2 border-gray-200 dark:border-gray-700">
            <h3 className="text-xl sm:text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100">{t('landing.pricing.free')}</h3>
            <div className="mb-4">
              <span className="text-3xl sm:text-4xl font-bold text-primary-600 dark:text-primary-400">0</span>
              <span className="text-gray-700 dark:text-gray-300"> {t('landing.pricing.month')}</span>
            </div>
            <ul className="text-left space-y-2 mb-6 text-sm sm:text-base">
              <li className="flex items-start">
                <span className="text-green-500 dark:text-green-400 mr-2">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Osnovno praćenje sati</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 dark:text-green-400 mr-2">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Pregled kalendara</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 dark:text-green-400 mr-2">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Jedan aktivan posao</span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-400 dark:text-gray-500 mr-2">✗</span>
                <span className="text-gray-600 dark:text-gray-400">Neograničeni poslovi</span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-400 dark:text-gray-500 mr-2">✗</span>
                <span className="text-gray-600 dark:text-gray-400">Napredne statistike</span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-400 dark:text-gray-500 mr-2">✗</span>
                <span className="text-gray-600 dark:text-gray-400">AI Pametno Uparivanje</span>
              </li>
            </ul>
            <button type="button" onClick={() => openAuthModal('register')} className="btn-secondary w-full">
              Registruj se
            </button>
          </div>

          {/* Basic Plan */}
          <div className="card text-center border-2 border-primary-500 dark:border-primary-400 transform scale-105 relative">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-primary-600 text-white px-4 py-1 rounded-full text-xs sm:text-sm font-semibold">
              {t('landing.pricing.mostPopular')}
            </div>
            <h3 className="text-xl sm:text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100">{t('landing.pricing.basic')}</h3>
            <div className="mb-4">
              <span className="text-3xl sm:text-4xl font-bold text-primary-600 dark:text-primary-400">599</span>
              <span className="text-gray-700 dark:text-gray-300"> {t('landing.pricing.month')}</span>
            </div>
            <ul className="text-left space-y-2 mb-6 text-sm sm:text-base">
              <li className="flex items-start">
                <span className="text-green-500 dark:text-green-400 mr-2">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Sve iz besplatnog plana</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 dark:text-green-400 mr-2">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Neograničeni poslovi</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 dark:text-green-400 mr-2">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Prioritet podrška</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 dark:text-green-400 mr-2">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Izveštaji i statistike</span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-400 dark:text-gray-500 mr-2">✗</span>
                <span className="text-gray-600 dark:text-gray-400">Napredne analitike</span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-400 dark:text-gray-500 mr-2">✗</span>
                <span className="text-gray-600 dark:text-gray-400">AI Pametno Uparivanje</span>
              </li>
            </ul>
            <button type="button" onClick={() => openAuthModal('register')} className="btn-primary w-full">
              Registruj se
            </button>
          </div>

          {/* Premium Plan */}
          <div className="card text-center border-2 border-primary-300 dark:border-primary-600 sm:col-span-2 lg:col-span-1">
            <h3 className="text-xl sm:text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100">{t('landing.pricing.premium')}</h3>
            <div className="mb-4">
              <span className="text-3xl sm:text-4xl font-bold text-primary-600 dark:text-primary-400">899</span>
              <span className="text-gray-700 dark:text-gray-300"> {t('landing.pricing.month')}</span>
            </div>
            <ul className="text-left space-y-2 mb-6 text-sm sm:text-base">
              <li className="flex items-start">
                <span className="text-green-500 dark:text-green-400 mr-2">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Sve iz Osnovnog plana</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 dark:text-green-400 mr-2">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Napredne analitike i izveštaji</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 dark:text-green-400 mr-2">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Prioritet na poslovima</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 dark:text-primary-400 mr-2 font-bold">🤖</span>
                <span className="font-semibold text-primary-600 dark:text-primary-400">AI Pametno Uparivanje</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 dark:text-green-400 mr-2">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Bez ograničenja</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 dark:text-green-400 mr-2">✓</span>
                <span className="text-gray-900 dark:text-gray-200">24/7 Premium podrška</span>
              </li>
            </ul>
            <button type="button" onClick={() => openAuthModal('register')} className="btn-primary w-full">
              Registruj se
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-gray-100">
            {t('landing.faq.title') || 'Često postavljana pitanja'}
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                question: t('landing.faq.q1') || 'Kako funkcioniše Nannity?',
                answer: t('landing.faq.a1') || 'Nannity je platforma koja povezuje roditelje i dadilje. Roditelji mogu da prate sate rada, automatski izračunavaju plaćanja i upravljaju kalendarom. Dadilje mogu da prate svoje poslove, zaradu i primaju notifikacije o novim prilikama.'
              },
              {
                question: t('landing.faq.q2') || 'Da li je platforma besplatna?',
                answer: t('landing.faq.a2') || 'Da, postoji besplatan plan sa osnovnim funkcijama. Takođe imamo Osnovni plan (499 RSD/mesec) i Premium plan (899 RSD/mesec) sa dodatnim funkcijama kao što je AI pametno uparivanje.'
              },
              {
                question: t('landing.faq.q3') || 'Kako funkcioniše automatsko plaćanje?',
                answer: t('landing.faq.a3') || 'Možete podesiti dinamiku plaćanja (mesečno, nedeljno, ili po određenim danima). Platforma automatski izračunava iznos na osnovu sati rada i satnice, a vi dobijate podsetnike pre datuma plaćanja.'
              },
              {
                question: t('landing.faq.q4') || 'Šta je AI pametno uparivanje?',
                answer: t('landing.faq.a4') || 'AI pametno uparivanje je funkcija dostupna Premium korisnicima koja automatski pronalazi idealnu dadilju ili posao na osnovu vašeg profila, preferenci, lokacije i istorije rada.'
              },
              {
                question: t('landing.faq.q5') || 'Mogu li da podelim kalendar sa porodicom?',
                answer: t('landing.faq.a5') || 'Da! Možete podeliti kalendar, plaćanja i veze sa bebisiterkama sa članovima porodice. Samo izaberite šta želite da podelite kada delite kalendar sa prijateljem.'
              },
              {
                question: t('landing.faq.q6') || 'Kako da počnem?',
                answer: t('landing.faq.a6') || 'Jednostavno se registrujte besplatno, popunite svoj profil i počnite da koristite platformu. Možete odmah da dodate bebisiterku ili da počnete da tražite posao kao dadilja.'
              }
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-all"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full px-6 sm:px-8 py-5 sm:py-6 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  aria-expanded={openFAQ === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <span className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 pr-4">
                    {faq.question}
                  </span>
                  {openFAQ === index ? (
                    <MdExpandLess className="text-2xl sm:text-3xl text-primary-600 dark:text-primary-400 flex-shrink-0" />
                  ) : (
                    <MdExpandMore className="text-2xl sm:text-3xl text-primary-600 dark:text-primary-400 flex-shrink-0" />
                  )}
                </button>
                <div
                  id={`faq-answer-${index}`}
                  className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${
                    openFAQ === index ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 sm:px-8 pt-3 pb-5 sm:pb-6 text-base sm:text-lg text-gray-700 dark:text-gray-300">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 sm:py-16 lg:py-20 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 tracking-tight mb-3">
              Kontakt
            </h2>
            <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300 max-w-2xl mx-auto">
              Imaš pitanje, predlog ili prijavu problema? Pošalji nam poruku i odgovorićemo što pre.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="group relative bg-white/70 dark:bg-gray-800/70 backdrop-blur-md rounded-2xl p-6 sm:p-7 border border-white/40 dark:border-slate-700/60 shadow-sm">
              <form onSubmit={submitContact} className="space-y-4">
                {/* Honeypot (bots fill this) */}
                <input
                  type="text"
                  value={contactForm.company}
                  onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Ime
                    </label>
                    <input
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-200 focus:border-primary-300 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                      placeholder="Tvoje ime"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-200 focus:border-primary-300 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                      placeholder="tvoj@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Naslov (opciono)
                  </label>
                  <input
                    type="text"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-200 focus:border-primary-300 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                    placeholder="Npr. Predlog za aplikaciju"
                    maxLength={120}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Poruka
                  </label>
                  <textarea
                    required
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-200 focus:border-primary-300 dark:bg-gray-800 dark:border-gray-700 dark:text-white min-h-[140px] resize-y"
                    placeholder="Napiši poruku..."
                    maxLength={2000}
                  />
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right">
                    {contactForm.message.length}/2000
                  </div>
                </div>

                {contactStatus.state !== 'idle' && contactStatus.message && (
                  <div
                    className={`text-sm rounded-xl px-4 py-3 border ${
                      contactStatus.state === 'success'
                        ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200'
                        : contactStatus.state === 'error'
                        ? 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200'
                        : 'bg-gray-50 border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {contactStatus.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={contactStatus.state === 'sending'}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {contactStatus.state === 'sending' ? 'Šaljem...' : 'Pošalji poruku'}
                </button>

                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Slanjem poruke pristaješ da te kontaktiramo email-om u vezi ovog upita.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="gradient-bg text-white py-12 sm:py-16 lg:py-20 dark:bg-gradient-to-br dark:from-primary-900 dark:via-primary-800 dark:to-pink-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-white">
            {t('landing.cta.title')}
          </h2>
          <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 opacity-90 max-w-2xl mx-auto text-white">
            {t('landing.cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button type="button" onClick={() => openAuthModal('register')} className="btn-secondary inline-block px-6 sm:px-8 py-3 sm:py-4">
              Registruj se
            </button>
            <button type="button" onClick={() => openAuthModal('login')} className="bg-white/20 hover:bg-white/30 text-white border-2 border-white/50 inline-block px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold transition-colors">
              Uloguj se
            </button>
          </div>
        </div>
      </section>
      </div>

      {/* Modal: Registruj se da vidiš detalje oglasa — prikazuje se kad gost klikne na rezultat pretrage */}
      {showRegisterPrompt && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50" onClick={() => setShowRegisterPrompt(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6 border border-gray-200 dark:border-gray-700" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Registruj se da vidiš sve detalje
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Kreiraj nalog ili se prijavi da bi mogao/la da vidiš pun opis oglasa, kontakt i ostale informacije.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                className="btn-primary text-center flex-1"
                onClick={() => {
                  setShowRegisterPrompt(false);
                  openAuthModal('register', registerPromptTarget);
                }}
              >
                Registruj se
              </button>
              <button
                type="button"
                className="btn-secondary text-center flex-1"
                onClick={() => {
                  setShowRegisterPrompt(false);
                  openAuthModal('login', registerPromptTarget);
                }}
              >
                Prijavi se
              </button>
            </div>
            <button
              type="button"
              className="mt-4 w-full text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              onClick={() => { setShowRegisterPrompt(false); setRegisterPromptTarget(null); }}
            >
              Zatvori
            </button>
          </div>
        </div>
      )}

      <Footer variant="landing" />
      <AuthModal
        isOpen={authModalOpen}
        initialMode={authModalMode}
        redirectTo={registerPromptTarget}
        onClose={() => {
          setAuthModalOpen(false);
          setRegisterPromptTarget(null);
        }}
      />
    </div>
  );
}
